import { useCallback, useMemo, useState } from 'react';
import { Contact, ContactWithDetails, ContactSortKey, ContactFilter } from '../types';
import * as contactsService from '../services/contacts.service';
import { withAuthRetry } from '../utils/withAuthRetry';

interface UseContactsProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
  canManageContacts: boolean;
}

export function useContacts({ accessToken, refreshToken, attemptRefresh, canManageContacts }: UseContactsProps) {
  // State
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedContact, setSelectedContact] = useState<ContactWithDetails | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<ContactFilter>('all');
  const [sort, setSort] = useState<{ column: ContactSortKey; direction: 'asc' | 'desc' }>({
    column: 'createdAt',
    direction: 'desc'
  });

  // Pagination state
  const [totalElements, setTotalElements] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);

  // Filtered and sorted contacts
  const filteredContacts = useMemo(() => {
    if (!Array.isArray(contacts)) {
      return [];
    }
    let result = contacts;

    // Apply local search filter (for real-time filtering while typing)
    if (search.trim()) {
      const searchLower = search.toLowerCase().trim();
      result = result.filter((c) => {
        return (
          (c.name && c.name.toLowerCase().includes(searchLower)) ||
          c.phoneNumber.includes(searchLower) ||
          (c.email && c.email.toLowerCase().includes(searchLower)) ||
          (c.company && c.company.toLowerCase().includes(searchLower))
        );
      });
    }

    // Apply local filter
    if (filter === 'qualified') {
      result = result.filter((c) => c.avgBantScore >= 3);
    } else if (filter === 'with_opportunities') {
      result = result.filter((c) => c.totalOpportunities > 0);
    }

    // Apply sort
    return [...result].sort((a, b) => {
      let comparison = 0;

      switch (sort.column) {
        case 'name':
          comparison = (a.name || '').localeCompare(b.name || '');
          break;
        case 'phone':
          comparison = a.phoneNumber.localeCompare(b.phoneNumber);
          break;
        case 'totalOpportunities':
          comparison = a.totalOpportunities - b.totalOpportunities;
          break;
        case 'avgBantScore':
          comparison = a.avgBantScore - b.avgBantScore;
          break;
        case 'lastOpportunityAt':
          const dateA = a.lastOpportunityAt ? new Date(a.lastOpportunityAt).getTime() : 0;
          const dateB = b.lastOpportunityAt ? new Date(b.lastOpportunityAt).getTime() : 0;
          comparison = dateA - dateB;
          break;
        case 'createdAt':
          comparison = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
          break;
      }

      return sort.direction === 'asc' ? comparison : -comparison;
    });
  }, [contacts, filter, search, sort]);

  // Toggle sort
  const toggleSort = useCallback((column: ContactSortKey) => {
    setSort((prev) => ({
      column,
      direction: prev.column === column && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  // Fetch contacts list
  const fetchContacts = useCallback(async (): Promise<{ success: boolean; error?: string }> => {
    if (!accessToken || !canManageContacts) return { success: false, error: 'Nao autorizado' };

    setLoading(true);

    try {
      const response = await withAuthRetry(
        () => contactsService.fetchContacts(accessToken!, { size: 100 }),
        refreshToken,
        attemptRefresh
      );
      setContacts(Array.isArray(response.items) ? response.items : []);
      setTotalElements(response.total);
      setTotalPages(response.totalPages);
      setCurrentPage(response.page);
      return { success: true };
    } catch (err) {
      return {
        success: false,
        error: err instanceof Error ? err.message : 'Erro inesperado ao buscar contatos'
      };
    } finally {
      setLoading(false);
    }
  }, [accessToken, refreshToken, attemptRefresh, canManageContacts]);

  // Fetch contact details by ID
  const fetchContactById = useCallback(
    async (contactId: string): Promise<{ success: boolean; error?: string; data?: ContactWithDetails }> => {
      if (!accessToken) return { success: false, error: 'Nao autenticado' };

      setDetailsLoading(true);

      try {
        const data = await withAuthRetry(
          () => contactsService.fetchContactById(contactId, accessToken!),
          refreshToken,
          attemptRefresh
        );
        setSelectedContact(data);
        return { success: true, data };
      } catch (err) {
        return {
          success: false,
          error: err instanceof Error ? err.message : 'Erro ao buscar detalhes do contato'
        };
      } finally {
        setDetailsLoading(false);
      }
    },
    [accessToken, refreshToken, attemptRefresh]
  );

  // Open details dialog
  const openDetails = useCallback(
    async (contact: Contact) => {
      setDetailsOpen(true);
      // Start with basic contact data, then fetch full details
      setSelectedContact({
        ...contact,
        opportunities: [],
        bantQualifications: []
      });
      await fetchContactById(contact.id);
    },
    [fetchContactById]
  );

  // Close details dialog
  const closeDetails = useCallback(() => {
    setDetailsOpen(false);
    setSelectedContact(null);
  }, []);

  return {
    // State
    contacts: filteredContacts,
    loading,
    selectedContact,
    detailsOpen,
    setDetailsOpen,
    detailsLoading,
    search,
    setSearch,
    filter,
    setFilter,
    sort,
    toggleSort,
    totalElements,
    totalPages,
    currentPage,

    // Actions
    fetchContacts,
    fetchContactById,
    openDetails,
    closeDetails
  };
}
